import React, { useCallback, useEffect, useState } from "react";
import Joyride, { type CallBackProps, EVENTS, STATUS, type Step } from "react-joyride";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { sampleProject } from "~/constants/sampleProject";
import type { Project } from "~/interface/project";
import { setCurrentProject, setTourCompleted, setTourStep } from "~/slices/auth";
import type { RootState } from "~/store";
import { TUTORIAL_ACTIVE_FLAG, TUTORIAL_EVENTS } from "~/utils/tutorialEvents";
import { type TourStepConfig, tourSteps } from "./tourSteps";

const cloneSampleProject = (): Project => {
  const outputs = Object.entries(sampleProject.outputs).reduce<Project["outputs"]>(
    (acc, [key, value]) => {
      acc[key] = { ...value };
      return acc;
    },
    {},
  );

  const estimationData = {
    ...sampleProject.estimationData,
    unit_prices: { ...sampleProject.estimationData.unit_prices },
    work_loads: { ...sampleProject.estimationData.work_loads },
  };

  const editableEstimationData = {
    ...sampleProject.editableEstimationData,
    unit_prices: { ...sampleProject.editableEstimationData.unit_prices },
    work_loads: { ...sampleProject.editableEstimationData.work_loads },
  };

  return {
    ...sampleProject,
    client: { ...sampleProject.client },
    vocabulary: sampleProject.vocabulary.map((item) => ({ ...item })),
    outputs,
    meta_prompts: [...sampleProject.meta_prompts],
    estimationData,
    editableEstimationData,
    costEffectivenessData: sampleProject.costEffectivenessData
      ? { ...sampleProject.costEffectivenessData }
      : undefined,
  };
};

const getStepLabel = (stepId: string): string => {
  switch (stepId) {
    case "project-top":
      return "プロジェクトトップ";
    case "sidebar-overview":
      return "サイドバー";
    case "dashboard-explanation":
      return "生成フロー";
    case "dashboard-upload":
      return "議事録の生成";
    case "summary":
      return "議事録";
    case "knowledge":
      return "ナレッジ";
    case "generate-system-proposal":
      return "システム概要書の生成";
    case "system-proposal":
      return "システム概要書";
    case "ai-chat":
      return "AIチャット";
    default:
      return stepId;
  }
};

const TUTORIAL_COMPLETED_KEY = "reqxign_tutorial_completed";

type PointerDirection = "up" | "down" | "left" | "right" | null;

const POINTER_LABELS: Record<Exclude<PointerDirection, null>, string> = {
  up: "上へスクロール",
  down: "下へスクロール",
  left: "左へスクロール",
  right: "右へスクロール",
};

const POINTER_ICONS: Record<Exclude<PointerDirection, null>, string> = {
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
};

const StepNavigator: React.FC<{
  steps: TourStepConfig[];
  current: number;
  onSelect: (index: number) => void;
  onClose: () => void;
}> = ({ steps, current, onSelect, onClose }) => (
  <div className="tutorial-step-navigator">
    <div className="tutorial-step-navigator__header">
      <span>ツアー目次</span>
      <button type="button" className="tutorial-step-navigator__close" onClick={onClose}>
        終わる
      </button>
    </div>
    <ol className="tutorial-step-navigator__list">
      {steps.map((step, index) => {
        const isActive = index === current;
        const isCompleted = index < current;
        const itemClass = [
          "tutorial-step-navigator__item",
          isActive ? "is-active" : "",
          isCompleted ? "is-completed" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <li key={step.id}>
            <button type="button" className={itemClass} onClick={() => onSelect(index)}>
              <span className="tutorial-step-navigator__badge">{index + 1}</span>
              <span className="tutorial-step-navigator__label">{getStepLabel(step.id)}</span>
              {isCompleted && (
                <span className="tutorial-step-navigator__check" aria-hidden="true">
                  ✓
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  </div>
);

interface TourControllerProps {
  isOpen: boolean;
  onClose: () => void;
}

const TourController: React.FC<TourControllerProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // tour_step is managed by Redux but not directly used in this component
  useSelector((state: RootState) => state.auth.tour_step);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const organization = useSelector((state: RootState) => state.auth.organization);

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [pointerDirection, setPointerDirection] = useState<PointerDirection>(null);

  const waitForElement = useCallback((selector: string, timeout = 5000): Promise<boolean> => {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        resolve(true);
        return;
      }

      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          observer.disconnect();
          resolve(true);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, timeout);
    });
  }, []);

  const goToStepIndex = useCallback(
    async (targetIndex: number) => {
      const targetStep = tourSteps[targetIndex] as TourStepConfig | undefined;
      if (!targetStep) return;

      const isDifferentRoute = targetStep.route && !location.pathname.includes(targetStep.route);

      if (isDifferentRoute) {
        setRun(false);
        navigate(targetStep.route!);

        if (targetStep.waitFor) {
          await waitForElement(targetStep.waitFor);
        } else {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        setStepIndex(targetIndex);
        dispatch(setTourStep(targetIndex));
        setRun(true);
      } else {
        if (targetStep.waitFor) {
          await waitForElement(targetStep.waitFor);
        }
        setStepIndex(targetIndex);
        dispatch(setTourStep(targetIndex));
      }
    },
    [dispatch, navigate, location.pathname, waitForElement],
  );

  const hasInitialized = React.useRef(false);

  const finishTour = useCallback(
    (didComplete: boolean) => {
      setRun(false);
      setStepIndex(0);

      dispatch(setTourCompleted(true));
      dispatch(setCurrentProject(null));
      dispatch(setTourStep(0));
      hasInitialized.current = false;

      if (typeof window !== "undefined") {
        localStorage.setItem(TUTORIAL_COMPLETED_KEY, didComplete ? "true" : "skipped");
        localStorage.removeItem(TUTORIAL_ACTIVE_FLAG);
        localStorage.setItem("reqxign_tutorial_show_create_prompt", "true");
        if (didComplete) {
          localStorage.setItem("reqxign_tutorial_dismissed", "true");
        }
      }

      onClose();

      const organizationId = currentUser?.organization_id || organization?.id;
      const targetPath = organizationId ? `/${organizationId}/home` : "/";

      setTimeout(() => {
        try {
          navigate(targetPath, { replace: true });
        } catch (e) {
          // Prevent unhandled exceptions in tests or edge cases
          console.error("Navigation error", e);
        }
      }, 0);
    },
    [dispatch, navigate, onClose, currentUser, organization],
  );

  const updatePointer = useCallback(() => {
    if (typeof window === "undefined" || !run) {
      setPointerDirection(null);
      return;
    }

    const activeStep = tourSteps[stepIndex];
    const targetElement = activeStep
      ? document.querySelector<HTMLElement>(activeStep.selector)
      : null;

    if (!targetElement) {
      setPointerDirection(null);
      return;
    }

    // Joyrideツールチップが表示されているかをチェック
    const joyrideTooltip = document.querySelector('[class*="react-joyride__tooltip"]');

    // ツールチップが表示されている場合、ツールチップの位置を基準にする
    const referenceElement = joyrideTooltip || targetElement;
    const rect = referenceElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    // 参照要素の可視部分を計算
    const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
    const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);

    // 可視率を計算（0.0〜1.0）
    const visibleHeightRatio = rect.height > 0 ? Math.max(0, visibleHeight) / rect.height : 1;
    const visibleWidthRatio = rect.width > 0 ? Math.max(0, visibleWidth) / rect.width : 1;

    // ツールチップまたはターゲット要素が50%以上見えていたら矢印を非表示
    const VISIBILITY_THRESHOLD = 0.5;
    if (visibleHeightRatio >= VISIBILITY_THRESHOLD && visibleWidthRatio >= VISIBILITY_THRESHOLD) {
      setPointerDirection(null);
      return;
    }

    // 参照要素が画面のどの位置にあるかを判定
    const isAboveScreen = rect.bottom < 0;
    const isBelowScreen = rect.top > windowHeight;
    const isLeftOfScreen = rect.right < 0;
    const isRightOfScreen = rect.left > windowWidth;

    // 完全に画面外の場合
    if (isAboveScreen) {
      setPointerDirection("up");
      return;
    }
    if (isBelowScreen) {
      setPointerDirection("down");
      return;
    }
    if (isLeftOfScreen) {
      setPointerDirection("left");
      return;
    }
    if (isRightOfScreen) {
      setPointerDirection("right");
      return;
    }

    // 部分的に見えている場合：より隠れている方向を判定
    const hiddenTop = Math.max(0, -rect.top);
    const hiddenBottom = Math.max(0, rect.bottom - windowHeight);
    const hiddenLeft = Math.max(0, -rect.left);
    const hiddenRight = Math.max(0, rect.right - windowWidth);

    // 縦方向と横方向でより隠れている方を選択
    const maxVerticalHidden = Math.max(hiddenTop, hiddenBottom);
    const maxHorizontalHidden = Math.max(hiddenLeft, hiddenRight);

    if (maxVerticalHidden > maxHorizontalHidden) {
      // 縦方向により隠れている
      setPointerDirection(hiddenTop > hiddenBottom ? "up" : "down");
    } else if (maxHorizontalHidden > 0) {
      // 横方向により隠れている
      setPointerDirection(hiddenLeft > hiddenRight ? "left" : "right");
    } else {
      // どちらも隠れていない（50%以上見えている）
      setPointerDirection(null);
    }
  }, [run, stepIndex]);

  const handlePointerClick = useCallback(() => {
    const activeStep = tourSteps[stepIndex];
    if (!activeStep) return;

    // Joyrideツールチップが表示されている場合、ツールチップまでスクロール
    const joyrideTooltip = document.querySelector<HTMLElement>('[class*="react-joyride__tooltip"]');
    const targetElement = document.querySelector<HTMLElement>(activeStep.selector);

    const scrollTarget = joyrideTooltip || targetElement;
    if (!scrollTarget) return;

    scrollTarget.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [stepIndex]);

  // ツアー開始時のみサンプルプロジェクトをセット
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true;
      dispatch(setCurrentProject(cloneSampleProject()));
      dispatch(setTourStep(0));
      setStepIndex(0);
      setRun(true);
    }

    if (!isOpen) {
      hasInitialized.current = false;
      setRun(false);
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("tutorial-step-navigator-visible");
    } else {
      document.body.classList.remove("tutorial-step-navigator-visible");
    }
    return () => {
      document.body.classList.remove("tutorial-step-navigator-visible");
    };
  }, [isOpen]);

  // サイドバーを無効化
  useEffect(() => {
    if (!isOpen) return;

    const sidebarElement = document.querySelector<HTMLElement>('[data-tour-id="tutorial-sidebar"]');
    const disabledButtons: Array<{ element: HTMLButtonElement; wasDisabled: boolean }> = [];
    const disabledAnchors: Array<{
      element: HTMLAnchorElement;
      handler: (event: MouseEvent) => void;
      originalTabIndex: string | null;
    }> = [];

    if (sidebarElement) {
      const buttons = sidebarElement.querySelectorAll<HTMLButtonElement>("button");
      buttons.forEach((button) => {
        if (button.dataset["tourAllow"] === "true") {
          return;
        }
        disabledButtons.push({ element: button, wasDisabled: button.disabled });
        button.disabled = true;
        button.dataset["tutorialDisabled"] = "true";
      });

      const anchors = sidebarElement.querySelectorAll<HTMLAnchorElement>("a");
      anchors.forEach((anchor) => {
        const handleAnchorClick = (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
        };
        const originalTabIndex = anchor.getAttribute("tabindex");
        anchor.addEventListener("click", handleAnchorClick);
        anchor.classList.add("is-disabled-by-tutorial");
        anchor.setAttribute("tabindex", "-1");
        anchor.setAttribute("aria-disabled", "true");
        disabledAnchors.push({
          element: anchor,
          handler: handleAnchorClick,
          originalTabIndex,
        });
      });
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      disabledButtons.forEach(({ element, wasDisabled }) => {
        element.disabled = wasDisabled;
        delete element.dataset["tutorialDisabled"];
      });

      disabledAnchors.forEach(({ element, handler, originalTabIndex }) => {
        element.removeEventListener("click", handler);
        element.classList.remove("is-disabled-by-tutorial");
        if (originalTabIndex !== null) {
          element.setAttribute("tabindex", originalTabIndex);
        } else {
          element.removeAttribute("tabindex");
        }
        element.removeAttribute("aria-disabled");
      });

      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleNextStep = () => {
      const nextIndex = stepIndex + 1;
      if (nextIndex < tourSteps.length) {
        goToStepIndex(nextIndex);
      }
    };

    window.addEventListener(TUTORIAL_EVENTS.NEXT_STEP, handleNextStep);
    return () => {
      window.removeEventListener(TUTORIAL_EVENTS.NEXT_STEP, handleNextStep);
    };
  }, [stepIndex, goToStepIndex]);

  useEffect(() => {
    const handleComplete = () => {
      finishTour(true);
    };

    window.addEventListener(TUTORIAL_EVENTS.COMPLETE_TOUR, handleComplete);
    return () => {
      window.removeEventListener(TUTORIAL_EVENTS.COMPLETE_TOUR, handleComplete);
    };
  }, [finishTour]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!run) {
      setPointerDirection(null);
      return;
    }

    const handleUpdate = () => updatePointer();
    const timer = window.setTimeout(() => updatePointer(), 0);
    window.addEventListener("scroll", handleUpdate, true);
    window.addEventListener("resize", handleUpdate);

    const observer = new MutationObserver(() => updatePointer());
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", handleUpdate, true);
      window.removeEventListener("resize", handleUpdate);
      observer.disconnect();
    };
  }, [run, updatePointer]);

  const handleJoyrideCallback = useCallback(
    async (data: CallBackProps) => {
      const { status, type, index, action } = data;

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        if (action === "next") {
          const nextIndex = index + 1;
          if (nextIndex >= tourSteps.length) {
            finishTour(true);
            return;
          }
          await goToStepIndex(nextIndex);
        } else if (action === "prev") {
          const prevIndex = index - 1;
          if (prevIndex >= 0) {
            await goToStepIndex(prevIndex);
          }
        }
      }

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        finishTour(status === STATUS.FINISHED);
      }
    },
    [goToStepIndex, finishTour],
  );

  // tourStepsをJoyride形式に変換
  const interactiveStepIds = new Set([
    "project-top",
    "sidebar-overview",
    "summary",
    "dashboard-step1",
    "dashboard-upload",
    "knowledge",
    "generate-system-proposal",
    "ai-chat",
  ]);

  const stepPlacementOverrides: Record<string, Step["placement"]> = {
    "sidebar-overview": "auto",
  };

  const joyrideSteps: Step[] = tourSteps.map((step) => ({
    target: step.selector,
    content: (
      <div>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: 700,
            marginBottom: "12px",
            color: "#1f2937",
            lineHeight: "1.4",
          }}
        >
          {getStepLabel(step.id)}
        </h3>
        <p style={{ fontSize: "14px", lineHeight: "1.7", color: "#475569" }}>{step.content}</p>
      </div>
    ),
    placement: stepPlacementOverrides[step.id] ?? ("auto" as const),
    offset: 12,
    disableBeacon: true,
    hideCloseButton: true,
    hideFooter: false,
    spotlightClicks: interactiveStepIds.has(step.id),
    spotlightPadding: interactiveStepIds.has(step.id) ? 6 : 8,
    locale: {
      back: <span>戻る</span>,
      next: "次へ",
      nextLabelWithProgress: "次へ（ステップ {step}/{steps}）",
      last: "完了",
    },
    styles: {
      options: {
        zIndex: 10000,
      },
    },
  }));

  const activePointer = pointerDirection
    ? {
        icon: POINTER_ICONS[pointerDirection],
        label: POINTER_LABELS[pointerDirection],
        direction: pointerDirection,
      }
    : null;

  if (!isOpen) return null;

  return (
    <>
      <Joyride
        steps={joyrideSteps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc={false}
        callback={handleJoyrideCallback}
        locale={{
          back: "戻る",
          close: "閉じる",
          last: "完了",
          next: "次へ",
          nextLabelWithProgress: "次へ（ステップ {step}/{steps}）",
          skip: "スキップ",
        }}
        styles={{
          options: {
            arrowColor: "#2563eb",
            backgroundColor: "#2563eb",
            overlayColor: "rgba(15, 23, 42, 0.18)",
            primaryColor: "#2563eb",
            textColor: "#0f172a",
            width: 400,
            zIndex: 99999,
          },
          overlay: {
            backgroundColor: "rgba(15, 23, 42, 0.18)",
          },
          tooltip: {
            borderRadius: 14,
            padding: "22px 24px",
            boxShadow: "0 24px 56px rgba(15, 23, 42, 0.24)",
            backgroundColor: "#ffffff",
          },
          tooltipContainer: {
            textAlign: "left",
          },
          tooltipContent: {
            padding: "0 0 16px 0",
          },
          tooltipFooter: {
            marginTop: 16,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 12,
            width: "100%",
          },
          buttonNext: {
            backgroundColor: "#2563eb",
            color: "#ffffff",
            borderRadius: 8,
            padding: "10px 20px",
            fontSize: 14,
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
          },
          buttonBack: {
            color: "#2563eb",
            backgroundColor: "transparent",
            borderRadius: 8,
            padding: "10px 20px",
            fontSize: 14,
            fontWeight: 600,
            border: "1px solid rgba(37, 99, 235, 0.35)",
            marginRight: "auto",
            order: 0,
          },
          buttonSkip: {
            display: "none",
          },
        }}
      />
      {run && (
        <StepNavigator
          steps={tourSteps}
          current={stepIndex}
          onSelect={goToStepIndex}
          onClose={() => finishTour(false)}
        />
      )}
      {run && activePointer && (
        <button
          type="button"
          className={`tutorial-pointer tutorial-pointer--${activePointer.direction}`}
          aria-label={activePointer.label}
          onClick={handlePointerClick}
        >
          <span className="tutorial-pointer__icon" aria-hidden="true">
            {activePointer.icon}
          </span>
          <span className="tutorial-pointer__label">{activePointer.label}</span>
        </button>
      )}
    </>
  );
};

export default TourController;
